import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  Box,
  IconButton,
} from "@mui/material";
import ArgonButton from "components/ArgonButton";
import ArgonTypography from "components/ArgonTypography";
import ArgonSelect from "components/ArgonSelect";
import EditIcon from "@mui/icons-material/Edit";
import { addCategory, editCategory } from "services/categoriesService";
import ArgonBox from "components/ArgonBox";
import { Image } from "@mui/icons-material";
import { toast } from "react-toastify";
import LanguageInput from "components/LanguageInput";
import { SUPPORTED_LANGUAGES } from "constants/languages";
import { premiumOptions } from "../consts";

const CategoryModal = ({ open, onClose, category, onSuccess }) => {
  const isEdit = Boolean(category);

  const [form, setForm] = useState({
    icon: null, // store actual file
    translations: [],
    is_premium: false,
  });
  const [iconPreview, setIconPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const fileInputRef = useRef();

  // Initialize translations for all supported languages
  const initializeTranslations = (existingTranslations = []) => {
    const translations = SUPPORTED_LANGUAGES.map((lang) => {
      const existing = existingTranslations.find(
        (t) => t.language === lang.code
      );
      return {
        language: { label: lang.name, value: lang.code },
        name: existing ? existing.name : "",
      };
    });
    return translations;
  };

  useEffect(() => {
    if (category) {
      // If editing, populate with existing translations or create default
      const existingTranslations = category.translations || [];
      const translations = initializeTranslations(existingTranslations);
      const premiumValue = premiumOptions.find(
        (option) => option.value === (category.is_premium || false)
      );
      setForm({
        icon: null,
        translations,
        is_premium: premiumValue,
      });
      setIconPreview(category.icon || null);
    } else {
      setForm({
        icon: null,
        translations: initializeTranslations(),
        is_premium: premiumOptions[0], // Default to "Basic"
      });
      setIconPreview(null);
    }
  }, [category, open]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleTranslationChange = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      translations: prev.translations.map((translation, i) =>
        i === index ? { ...translation, [field]: value } : translation
      ),
    }));
  };

  const handleEditImageClick = () => {
    fileInputRef.current.click();
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleChange("icon", file);
      const reader = new FileReader();
      reader.onload = (ev) => {
        setIconPreview(ev.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    // Validate that ALL translations have names
    const emptyTranslations = form.translations.filter((t) => !t.name.trim());

    if (emptyTranslations.length > 0) {
      toast.error(
        "All language fields are required. Please fill in category names for all languages."
      );
      return;
    }

    const validTranslations = form.translations.map((t) => ({
      language: t.language.value,
      name: t.name.trim(),
    }));

    // Validate that an icon is selected
    if (!isEdit && !form.icon) {
      toast.error("Please set an icon for the category");
      return;
    }

    if (isEdit && !form.icon && !category.icon) {
      toast.error("Please set an icon for the category");
      return;
    }

    const payload = new FormData();
    payload.append("translations", JSON.stringify(validTranslations));
    payload.append("is_premium", form.is_premium.value);
    if (form.icon) {
      payload.append("icon", form.icon);
    }

    try {
      setLoading(true);

      const response = isEdit
        ? await editCategory(category._id, payload)
        : await addCategory(payload);

      if (response.success) {
        onSuccess(response.data);
        onClose();
      } else {
        toast.error(response.message || "Failed to save category");
      }
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: "16px",
          padding: "40px 32px",
          textAlign: "center",
        },
      }}
    >
      <DialogContent>
        <input
          type="file"
          accept="image/jpeg, image/png, image/jpg"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleImageChange}
        />

        <ArgonTypography variant="h4" mb={3}>
          {isEdit ? "Edit Category" : "Add New Category"}
        </ArgonTypography>

        {/* Image Preview */}
        <Box
          sx={{
            width: 72,
            height: 72,
            mx: "auto",
            mb: 3,
            position: "relative",
            borderRadius: "12px",
            backgroundColor: "#F1F5F9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ArgonBox display="flex" alignItems="center" justifyContent="center">
            {iconPreview ? (
              <img
                src={iconPreview}
                alt="icon"
                style={{
                  borderRadius: 5,
                  width: "60%",
                  height: "60%",
                }}
              />
            ) : (
              <Image fontSize="large" color="primary" />
            )}
          </ArgonBox>
          <IconButton
            onClick={handleEditImageClick}
            sx={{
              position: "absolute",
              bottom: -10,
              right: -10,
              bgcolor: "#2d3f50",
              border: "1px solid #2d3f50",
              borderRadius: "8px",
              width: 25,
              height: 25,
            }}
          >
            <EditIcon fontSize="small" color="white" />
          </IconButton>
        </Box>

        {/* Premium Select */}
        <Box mb={3}>
          <ArgonTypography
            variant="h6"
            fontWeight="bold"
            mb={1}
            textAlign="left"
          >
            Feature
          </ArgonTypography>
          <ArgonSelect
            value={form.is_premium}
            options={premiumOptions}
            onChange={(selectedOption) => handleChange("is_premium", selectedOption)}
            placeholder="Select category type"
            isDisabled={loading}
            width="100%"
          />
        </Box>

        {/* Language Translations */}
        <Box mb={3}>
          <ArgonTypography
            variant="h6"
            fontWeight="bold"
            mb={2}
            textAlign="left"
          >
            Category Names in Different Languages
          </ArgonTypography>
          <ArgonTypography
            variant="body2"
            color="textSecondary"
            mb={3}
            textAlign="left"
          >
            Fill in the category name for each language. At least one language
            is required.
          </ArgonTypography>

          {form.translations.map((translation, index) => (
            <Box key={index} mb={2}>
              <LanguageInput
                language={translation.language}
                name={translation.name}
                onLanguageChange={(value) =>
                  handleTranslationChange(index, "language", value)
                }
                onNameChange={(e) =>
                  handleTranslationChange(index, "name", e.target.value)
                }
                disabled={loading}
                languageLabel="Language"
                nameLabel="Category Name"
                languagePlaceholder="Select Language"
                namePlaceholder="Enter category name in this language"
              />
            </Box>
          ))}
        </Box>

        {/* Buttons */}
        <Box display="flex" justifyContent="center" gap={2}>
          <ArgonButton
            variant="outlined"
            color="secondary"
            onClick={onClose}
            disabled={loading}
            sx={{ minWidth: 100, backgroundColor: "#ffff", color: "#2d3f50" }}
          >
            Cancel
          </ArgonButton>
          <ArgonButton
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              minWidth: 100,
              backgroundColor: "#2d3f50",
              color: "#ffff",
              "&:hover": { backgroundColor: "#2d3f50" },
            }}
          >
            {loading ? "Saving..." : isEdit ? "Update" : "Add"}
          </ArgonButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CategoryModal;
